/**
 * @file        src/pages/staff/StaffList.jsx
 * @description Staff List (CRUD) — admin-managed staff accounts with module
 *              access. Mirrors the legacy screen: #, Name, Email, Mobile,
 *              Status, Updated On, Action (edit / delete).
 * @author      Kinetika Labs
 * @created     2026-07-03
 * @changelog
 *   - 2026-07-03 Claude — Initial (Staff Management).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, del } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';

function fmtDateTime(s) {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export default function StaffList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');

  const qc = useQueryClient();
  const params = { page, pageSize };
  if (applied) params.q = applied;

  const { data, isLoading } = useQuery({
    queryKey: qk.staff.list(params),
    queryFn: () => get('/staff', { params }),
    placeholderData: keepPreviousData,
  });

  const removeMut = useMutation({
    mutationFn: (id) => del(`/staff/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Staff member removed.');
      qc.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete'),
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  async function onDelete(row) {
    const name = `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.email;
    if (!(await confirm(`Delete staff member “${name}”?`))) return;
    removeMut.mutate(row.id);
  }

  return (
    <>
      <PageHead
        title="Staff List"
        subtitle="Staff Management · admin panel staff accounts"
        actions={<Link to="/staff/new" className="btn primary"><Icon name="plus" /> Add Staff</Link>}
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}>
          <div className="field">
            <label>Name / Email / Mobile</label>
            <input placeholder="Search staff…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Name</th><th>Email</th><th>Mobile</th><th>Modules</th><th>Status</th><th>Updated On</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} className="empty">No staff yet. Click “Add Staff” to create one.</td></tr>
                ) : rows.map((s, idx) => (
                  <tr key={s.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td><b>{`${s.first_name || ''} ${s.last_name || ''}`.trim() || '—'}</b></td>
                    <td>{s.email || <span className="muted">—</span>}</td>
                    <td className="mono">{s.mobile || '—'}</td>
                    <td>{Array.isArray(s.modules) && s.modules.length
                      ? <span title={s.modules.join(', ')}>{s.modules.length} module{s.modules.length > 1 ? 's' : ''}</span>
                      : <span className="muted">All / none set</span>}
                    </td>
                    <td><Chip tone={s.is_active ? 'ok' : 'rej'}>{s.is_active ? 'Active' : 'Inactive'}</Chip></td>
                    <td>{fmtDateTime(s.updated_at)}</td>
                    <td className="row-act">
                      <Link to={`/staff/${s.id}/edit`} className="ib edit" aria-label="Edit"><Icon name="edit" /></Link>
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(s)}><Icon name="trash" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
